import { FC } from 'react'
import { MapDispatchToPropsFunction } from 'react-redux'
import { State } from 'interfaces/state'
import {
  FeedPeerGroupsHeader,
  FeedPeerGroupsHeaderStateProps,
  FeedPeerGroupsHeaderDispatchProps,
  FeedPeerGroupsHeaderOwnProps,
} from 'components/features/socialFeed/FeedPeerGroupsHeader/FeedPeerGroupsHeader'
import { setShownSidebarPaneActionCreator } from 'actions/sidebarActions'
import {
  feedSelectedFilterSelector,
  filterRelationsSelector,
  createFilterHasNewPostsSelector,
  allFeedFilterQueriesSelector,
  isAllFilterUpdatesLoadedSelector,
} from 'selectors/feedSelectors'
import { selectFeedFilterActionCreator, getFiltersStatusActionCreator } from 'actions/feedActions'
import { peersRelationSelector } from 'selectors/peergroupSelectors'
import { shownSidebarPaneSelector } from 'selectors/globalSelectors'
import { getRelationFilter } from 'helpers/feedHelpers'
import { YOUR_POSTS_FILTER, FEEDGROUPS_FILTERS } from 'constants/feed'
import { connectOptionally, OptionalRendererProps } from 'tangle-shared'
import { useShallowSelector } from 'helpers/reactReduxHelpers'
import { createOptionalRenderer } from 'helpers/tangleSharedHelpers'
import { connectLoadable } from 'helpers/loadableHelpers'
import { SimpleGuard } from 'interfaces/guards'

const optionalRenderer = createOptionalRenderer<FeedPeerGroupsHeaderStateProps>()
const mapStateToProps = (state: State): OptionalRendererProps<FeedPeerGroupsHeaderStateProps> => {
  const yourRelation = peersRelationSelector(state)
  const { everyone, followed_posts, followed_people, followed_tags } = FEEDGROUPS_FILTERS

  if (!yourRelation) {
    return optionalRenderer.fallback()
  }

  const otherRelations = filterRelationsSelector(state)

  const hasNewPostsSelector = createFilterHasNewPostsSelector()

  const others = otherRelations.filter((relation) => {
    const filter = getRelationFilter(relation)
    return !!hasNewPostsSelector(state, { filter })
  })

  const hasNewPosts = {
    you: !!hasNewPostsSelector(state, { filter: YOUR_POSTS_FILTER }),
    peers: !!hasNewPostsSelector(state, { filter: getRelationFilter(yourRelation) }),
    everyone: !!hasNewPostsSelector(state, { filter: everyone }),
    followed_people: !!hasNewPostsSelector(state, { filter: followed_people }),
    followed_tags: !!hasNewPostsSelector(state, { filter: followed_tags }),
    followed_posts: !!hasNewPostsSelector(state, { filter: followed_posts }),
    others: others.length > 0,
  }

  return optionalRenderer.render({
    yourRelation,
    selected: feedSelectedFilterSelector(state),
    hasNewPosts,
    shownSidebarPane: shownSidebarPaneSelector(state),
  })
}

const mapDispatchToProps: MapDispatchToPropsFunction<FeedPeerGroupsHeaderDispatchProps, FeedPeerGroupsHeaderOwnProps> = (dispatch) => {
  return {
    onSelectFilter: (filter) => dispatch(selectFeedFilterActionCreator(filter)),
    onTogglePeergroups: (paneId) => dispatch(setShownSidebarPaneActionCreator(paneId)),
  }
}

const FeedPeerGroupsHeaderInnerContainer = connectOptionally<
  FeedPeerGroupsHeaderStateProps,
  FeedPeerGroupsHeaderDispatchProps,
  FeedPeerGroupsHeaderOwnProps
>(mapStateToProps, mapDispatchToProps)(FeedPeerGroupsHeader)

const queriesGuard: SimpleGuard<FeedPeerGroupsHeaderOwnProps> = {
  name: 'feedQueriesGuard',
  checker: isAllFilterUpdatesLoadedSelector,
  loader: (dispatch, ownProps) => {
    dispatch(getFiltersStatusActionCreator.request(ownProps.queries))
  },
}

const FeedPeerGroupsHeaderLoadableWrapper = connectLoadable<
  {},
  {},
  FeedPeerGroupsHeaderOwnProps
>([queriesGuard], () => null)(() => ({}), () => ({}))(FeedPeerGroupsHeaderInnerContainer)


export const FeedPeerGroupsHeaderContainer: FC = () => {
  const allFilterQueries = useShallowSelector(allFeedFilterQueriesSelector)

  return (
    <FeedPeerGroupsHeaderLoadableWrapper queries={ allFilterQueries } />
  )
}
